import { FC } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  capitalizeFirstLetter,
  colors,
  formatXAxis,
  formatYAxis,
  IAiChart,
  IBarConfig,
} from "./chartConfig";
import { customTokens } from "../../theme/theme";
import { useStyles } from "./AiGraphs.styles";

const AiLineChart: FC<IAiChart> = ({ config, chartData }) => {
  const styles = useStyles();
  const { data } = config as IBarConfig;

  const xKey = data.x.field;
  const xLabel = data.x.label;

  const yKey = data.y.field;
  const yLabel = data.y.label;

  const seriesKey = data.series;

  // If we have series, pivot data into multiple y-fields
  let transformedData = chartData;
  let seriesValues: string[] = [];

  if (seriesKey) {
    seriesValues = Array.from(new Set(chartData.map((d) => d[seriesKey])));

    transformedData = chartData.reduce((acc: any[], curr) => {
      const existing = acc.find((item) => item[xKey] === curr[xKey]);
      if (existing) {
        existing[curr[seriesKey]] = curr[yKey];
      } else {
        acc.push({
          [xKey]: curr[xKey],
          [curr[seriesKey]]: curr[yKey],
        });
      }
      return acc;
    }, []);
  }

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={transformedData} margin={{ left: 40, right: 25 }}>
        <CartesianGrid
          stroke={customTokens.colorNeutralBackground4}
          horizontal={true}
          vertical={false}
        />

        <XAxis
          tickFormatter={(v) => formatXAxis(v)}
          dataKey={xKey}
          axisLine={false}
          tickLine={false}
          interval={0}
          label={{
            value: xLabel,
            position: "bottom",
            style: {
              textAnchor: "middle",
              fontWeight: 700,
              fill: customTokens.fontDarkGrayBody,
            },
          }}
        />

        <YAxis
          tickFormatter={(v) => formatYAxis(v)}
          axisLine={false}
          tickLine={false}
          label={{
            value: yLabel,
            angle: -90,
            position: "insideLeft",
            offset: -20,
            style: {
              textAnchor: "middle",
              fontWeight: 700,
              fill: customTokens.fontDarkGrayBody,
            },
          }}
        />

        <Tooltip formatter={(v) => formatYAxis(v)} />

        <Legend
          wrapperStyle={{ paddingTop: 30 }}
          content={({ payload }) => (
            <div className={styles.legendContainer}>
              {payload?.map((entry, index) => (
                <div key={`item-${index}`} className={styles.legendWrapper}>
                  <span
                    className={styles.legendIcon}
                    style={{ backgroundColor: entry.color }}
                  />
                  <span>{capitalizeFirstLetter(entry.value ?? "")}</span>
                </div>
              ))}
            </div>
          )}
        />

        {seriesKey ? (
          seriesValues.map((s, idx) => (
            <Line
              key={s}
              dataKey={s}
              stroke={colors[idx % colors.length]}
              strokeWidth={2}
              dot={{
                r: 4,
                fill: colors[idx % colors.length],
                strokeWidth: 0,
              }}
            />
          ))
        ) : (
          <Line
            dataKey={yKey}
            fill={customTokens.UNFCCCBrandGraphLevel1}
            strokeWidth={2}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default AiLineChart;
